// src/components/admin/EventsStatsWidget.js
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Box,
  Avatar,
  LinearProgress,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import {
  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  BarChart as BarChartIcon,
  Sports as SportsIcon,
  Pool as PoolIcon,
  DirectionsRun as RunIcon,
  SportsBasketball as BasketballIcon
} from '@mui/icons-material';

const EventsStatsWidget = ({ data }) => {
  const [tabValue, setTabValue] = useState(0);

  if (!data) return null;

  const eventStatusData = [
    {
      status: 'Completados',
      count: data.completed || 0,
      percentage: ((data.completed || 0) / data.total * 100).toFixed(1),
      color: 'success',
      icon: <CheckCircleIcon />
    },
    {
      status: 'Activos',
      count: data.active || 0,
      percentage: ((data.active || 0) / data.total * 100).toFixed(1),
      color: 'primary',
      icon: <EventIcon />
    },
    {
      status: 'Próximos',
      count: data.upcoming || 0,
      percentage: ((data.upcoming || 0) / data.total * 100).toFixed(1),
      color: 'warning',
      icon: <ScheduleIcon />
    }
  ];

  const categoryData = [
    {
      name: 'Atletismo',
      count: data.byCategory?.athletics || 0,
      icon: <RunIcon />,
      color: 'primary',
      percentage: ((data.byCategory?.athletics || 0) / data.total * 100).toFixed(1)
    },
    {
      name: 'Natación',
      count: data.byCategory?.swimming || 0,
      icon: <PoolIcon />,
      color: 'info',
      percentage: ((data.byCategory?.swimming || 0) / data.total * 100).toFixed(1)
    },
    {
      name: 'Fútbol',
      count: data.byCategory?.football || 0,
      icon: <SportsIcon />,
      color: 'success',
      percentage: ((data.byCategory?.football || 0) / data.total * 100).toFixed(1)
    },
    {
      name: 'Baloncesto',
      count: data.byCategory?.basketball || 0,
      icon: <BasketballIcon />,
      color: 'warning',
      percentage: ((data.byCategory?.basketball || 0) / data.total * 100).toFixed(1)
    }
  ];

  const recentEvents = [
    { name: 'Maratón Ciudad', date: '2024-12-05', participants: 450, status: 'completed' },
    { name: 'Copa Juvenil Natación', date: '2024-12-08', participants: 120, status: 'active' },
    { name: 'Torneo Baloncesto', date: '2024-12-15', participants: 85, status: 'upcoming' },
    { name: 'Atletismo Escolar', date: '2024-12-20', participants: 200, status: 'upcoming' }
  ];

  const getStatusColor = (status) => {
    const colors = {
      completed: 'success',
      active: 'primary',
      upcoming: 'warning',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      completed: 'Completado',
      active: 'En curso',
      upcoming: 'Próximo',
      cancelled: 'Cancelado'
    };
    return labels[status] || status;
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title="🏆 Estadísticas de Eventos"
        subheader={`${data.total} eventos totales en la plataforma`}
        action={
          <Chip 
            label={`${data.active} activos`} 
            color="primary" 
            size="small"
          />
        }
      />
      
      <CardContent>
        {/* Tabs para diferentes vistas */}
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Estado" />
          <Tab label="Categorías" />
          <Tab label="Recientes" />
        </Tabs>

        {/* Tab 1: Estado de eventos */}
        {tabValue === 0 && (
          <Box>
            {/* Resumen general */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.light', borderRadius: 2, color: 'white' }}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                {data.total}
              </Typography>
              <Typography variant="body1">
                Total de eventos en la plataforma
              </Typography>
              <Typography variant="caption">
                +{Math.round(data.total * 0.12)} eventos este mes
              </Typography>
            </Box>

            {/* Estado de eventos */}
            <Grid container spacing={2}>
              {eventStatusData.map((item, index) => (
                <Grid item xs={12} key={index}>
                  <Paper sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ bgcolor: `${item.color}.main` }}>
                          {item.icon}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight="bold">
                            {item.count}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.status}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ minWidth: 100 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <LinearProgress
                            variant="determinate"
                            value={parseFloat(item.percentage)}
                            color={item.color}
                            sx={{ width: 60, height: 8, borderRadius: 4 }}
                          />
                          <Typography variant="caption">
                            {item.percentage}%
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Tab 2: Categorías de eventos */}
        {tabValue === 1 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Distribución por Deporte
            </Typography>
            
            <Grid container spacing={2}>
              {categoryData.map((category, index) => (
                <Grid item xs={6} key={index}>
                  <Paper sx={{ p: 2, textAlign: 'center', border: 1, borderColor: 'divider' }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: `${category.color}.main`, 
                        mx: 'auto', 
                        mb: 1,
                        width: 40,
                        height: 40
                      }}
                    >
                      {category.icon}
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold" color={`${category.color}.main`}>
                      {category.count}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      {category.name}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={parseFloat(category.percentage)}
                      color={category.color}
                      sx={{ height: 6, borderRadius: 3, mb: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {category.percentage}% del total
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Estadísticas adicionales */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                📈 Insights de Categorías
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • El atletismo representa el {categoryData[0]?.percentage}% de todos los eventos
                <br />
                • Las categorías acuáticas están creciendo un 15% mensual
                <br />
                • Los deportes de equipo generan mayor participación promedio
              </Typography>
            </Box>
          </Box>
        )}

        {/* Tab 3: Eventos recientes */}
        {tabValue === 2 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Actividad Reciente
            </Typography>
            
            <List dense>
              {recentEvents.map((event, index) => (
                <ListItem key={index} divider>
                  <ListItemIcon>
                    <CalendarIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight="bold">
                          {event.name}
                        </Typography>
                        <Chip
                          label={getStatusLabel(event.status)}
                          color={getStatusColor(event.status)}
                          size="small"
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        📅 {new Date(event.date).toLocaleDateString('es-ES')} • 
                        👥 {event.participants} participantes
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>

            {/* Métricas rápidas */}
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Typography variant="h6" color="primary.main">
                      855
                    </Typography>
                    <Typography variant="caption">Total Participantes</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Typography variant="h6" color="success.main">
                      97%
                    </Typography>
                    <Typography variant="caption">Tasa Finalización</Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box textAlign="center">
                    <Typography variant="h6" color="info.main">
                      4.8★
                    </Typography>
                    <Typography variant="caption">Satisfacción</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default EventsStatsWidget;